import { lazy } from 'react'
import type { ComponentType, ReactNode } from 'react'

/* ------------------------------------------------------------------
   ثبت ماژول‌های Frontend — سند مادر بند ۵.۲ و ۱۱.۱
   ------------------------------------------------------------------
   · هر ماژول یک Feature Package با route، صفحات و مجوزهای خودش است.
   · بارگذاری Route تنبل (Lazy) است.
   · Deep Link پایدار: /app/<module>/<resource>/<id>
   · منو بر اساس Permission تولید می‌شود — اما نبودن منو جایگزین
     کنترل Backend نیست (بند ۱۱.۱).
   · ❌ Runtime Module Federation در فاز فعلی ممنوع است — این ثبت
     ایستا و در زمان Build حل می‌شود.
   ------------------------------------------------------------------ */

/** یک بخش داخل ماژول — سطح دوم منو */
export interface ModuleChild {
  /** برچسب فارسی */
  title: string
  /** مسیر نسبی زیر مسیر ماژول؛ '' یعنی صفحه Landing خود ماژول */
  path: string
  /**
   * مجوز لازم برای دیدن این آیتم. اگر خالی باشد، همان دسترسی ماژول کافی است.
   * ⚠️ فقط کنترل نمایش است — Backend مستقل بررسی می‌کند (بند ۷.۲).
   */
  permission?: string
  /** یکی از این‌ها کافی است */
  anyOf?: string[]
  /**
   * آیکون سه‌بعدی سرصفحهٔ همین بخش — نام فایل زیر public/icons/3d
   * بدون پسوند. اگر نباشد، سرصفحه به `icon3d` خود ماژول برمی‌گردد.
   *
   * ⚠️ فقط رندرهای بدون متنِ پخته‌شده اینجا بیایند: نوشتهٔ داخل تصویر
   * در ۵۶px ناخوانا می‌شود و کنار عنوانِ واقعی هم تکراری است.
   */
  icon3d?: string
  /**
   * از فهرست سایدبار حذف می‌شود ولی در نگاشت آیکون می‌ماند.
   *
   * برای صفحه‌هایی مثل «جزئیات گزارش» یا «ثبت کارمند» که مقصد ناوبری
   * نیستند (از دکمه یا کلیک روی ردیف باز می‌شوند) ولی سرصفحه‌شان باید
   * آیکون خودش را داشته باشد.
   */
  hidden?: boolean
}

export interface ModuleDefinition {
  /** کلید فنی — با پیشوند مجوزها یکسان است (بند ۷.۱) */
  key: string
  /** نام نمایشی فارسی */
  title: string
  /** مسیر پایه زیر /app */
  path: string
  /** آیکون خطی تک‌رنگ — سایدبار و نوار پایین (از currentColor پیروی می‌کند) */
  icon: ReactNode
  /**
   * آیکون سه‌بعدی کاشی لانچر — نام فایل زیر public/icons/3d بدون پسوند.
   * فقط جایی به کار می‌رود که آیکون بزرگ رندر می‌شود؛ در سایدبار از
   * `icon` استفاده می‌شود چون رنگ ثابتِ رندر سه‌بعدی با قرص فعال و
   * پوسته تاریک هماهنگ نمی‌شود و در ۱۸px هم جزئیاتش گم می‌شود.
   */
  icon3d?: string
  /** مالک دامنه — از Module Catalog */
  owner: string
  /**
   * توضیح کوتاه کارکرد ماژول — زیرنویس کاشی لانچر.
   * برای ماژول‌های مهاجرت‌نکرده به‌جای نام مالک نمایش داده می‌شود،
   * چون کاربر هنوز مالک را لازم ندارد و می‌خواهد بداند این ماژول
   * وقتی آمد چه کاری می‌کند.
   */
  tagline: string
  /** پروژه مبدأ که این ماژول از آن مهاجرت می‌کند */
  sourceProject: string
  /** فاز مهاجرت طبق MIGRATION_BACKLOG.md */
  phase: number
  /** تا زمان مهاجرت، ماژول در Shell غیرفعال نمایش داده می‌شود */
  enabled: boolean
  component: ComponentType
  /** ترتیب نمایش در منو */
  order: number
  /** بخش‌های داخلی ماژول — سطح دوم منو */
  children: ModuleChild[]
  /**
   * فقط «مدیر سیستم» این ماژول را می‌بیند — یعنی نقشی که
   * `is_super_admin` است.
   *
   * ==================================================================
   * 🔴 چرا جدا از `permission` اعلام می‌شود:
   *
   * فیلترِ منو تا امروز فقط مجوزِ زیرمنو را می‌سنجید. برای «سطح
   * دسترسی» آن مجوز `platform.role.view` است که امروز تنها به
   * `system_admin` داده شده — پس نتیجه *اتفاقاً* درست بود.
   *
   * ⚠️ ولی این مجوز از راه همان کنسول به هر نقشی قابل دادن است. روزی
   * که کسی `platform.role.view` را به «منابع انسانی» بدهد، دکمهٔ سطح
   * دسترسی هم برایش ظاهر می‌شود — بی‌آنکه کسی چنین تصمیمی گرفته باشد.
   *
   * 🔴 خواسته «فقط مدیر سیستم ببیند» یک قاعدهٔ ثابت است، نه پیامدِ
   * جانبیِ جدولِ نقش‌ها. پس صریح نوشته می‌شود تا با تغییرِ اعطاها
   * نلغزد.
   *
   * ⚠️ این هم فقط کنترل نمایش است — Backend مستقل بررسی می‌کند
   * (بند ۷.۲).
   * ==================================================================
   */
  superAdminOnly?: boolean
  /**
   * استثنای صریحِ `superAdminOnly` — نقش‌هایی که با وجود آن، ماژول را
   * می‌بینند.
   *
   * ==================================================================
   * 🔴 چرا لازم شد:
   *
   * «خدمات و پشتیبانی» `superAdminOnly` است، ولی نقشِ «هلپ‌دسک» دقیقاً
   * برای کار در همین ماژول ساخته شده. بدون این استثنا، کارشناسِ
   * هلپ‌دسک همهٔ مجوزها را می‌گرفت و همچنان هیچ منو و مسیری نمی‌دید —
   * یعنی نقش از روز اول بی‌اثر بود.
   *
   * ⚠️ عمداً فهرستِ **نقش** است و نه مجوز: اگر با مجوز سنجیده می‌شد،
   * دوباره همان لغزشی پیش می‌آمد که `superAdminOnly` برای جلوگیری از
   * آن ساخته شد — دادنِ یک مجوز به نقشی دیگر، در ماژول را هم باز
   * می‌کرد.
   *
   * 🔴 و مثل بقیهٔ این فایل، فقط کنترلِ نمایش است. Backend هر درخواست
   * را مستقل می‌سنجد.
   * ==================================================================
   */
  alsoVisibleToRoles?: string[]
}

/* ---------------- آیکون‌ها ----------------
   مسیرها از Tabler Icons گرفته شده‌اند (نسخه ۳.۴۶، مجوز MIT — بدون
   الزام اعتباردهی، مجاز برای استفاده تجاری).

   ⚠️ عمداً پکیج @tabler/icons-react نصب *نشده*: آن بسته ۶۶ مگابایت
   باز می‌شود برای همین چند آیکون، و این فایل طبق بند ۲۰ باید بدون
   وابستگی خارجی بماند. پس فقط مسیرِ همان آیکون‌های لازم اینجا inline
   شده‌اند. برای افزودن آیکون تازه، از tabler.io/icons مسیر outline را
   بردارید و به همین شکل اضافه کنید.

   شبکه Tabler ۲۴×۲۴ با قلم ۲px است — همان viewBox قبلی، فقط ضخامت
   قلم از ۱٫۶ به ۱٫۷۵ رسید تا هم به طرح اصلی وفادار بماند و هم در
   ۱۸px بیش از حد سنگین نشود. */

const icon = (path: ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" className="size-[18px] shrink-0" aria-hidden="true">
    {path}
  </svg>
)

const strokeProps = {
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

export const ICONS = {
  /* tabler: layout-dashboard */
  dashboard: icon(
    <>
      <path d="M5 4h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-6a1 1 0 0 1 1 -1" {...strokeProps} />
      <path d="M5 16h4a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" {...strokeProps} />
      <path d="M15 12h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-6a1 1 0 0 1 1 -1" {...strokeProps} />
      <path d="M15 4h4a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1v-2a1 1 0 0 1 1 -1" {...strokeProps} />
    </>,
  ),
  /* tabler: inbox */
  inbox: icon(
    <>
      <path d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -12" {...strokeProps} />
      <path d="M4 13h3l3 3h4l3 -3h3" {...strokeProps} />
    </>,
  ),
  /* tabler: message-circle */
  chat: icon(
    <path d="M3 20l1.3 -3.9c-2.324 -3.437 -1.426 -7.872 2.1 -10.374c3.526 -2.501 8.59 -2.296 11.845 .48c3.255 2.777 3.695 7.266 1.029 10.501c-2.666 3.235 -7.615 4.215 -11.574 2.293l-4.7 1" {...strokeProps} />,
  ),
  /* tabler: sitemap — چارتِ سازمانی، سطحِ اولِ سایدبار (۱۴۰۵/۰۸/۱۱) */
  orgChart: icon(
    <>
      <path d="M3 17a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" {...strokeProps} />
      <path d="M15 17a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" {...strokeProps} />
      <path d="M9 5a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v2a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" {...strokeProps} />
      <path d="M6 15v-1a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v1" {...strokeProps} />
      <path d="M12 9l0 3" {...strokeProps} />
    </>,
  ),
  /* tabler: users */
  people: icon(
    <>
      <path d="M5 7a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" {...strokeProps} />
      <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" {...strokeProps} />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" {...strokeProps} />
      <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" {...strokeProps} />
    </>,
  ),
  /* tabler: calendar-check */
  leave: icon(
    <>
      <path d="M11.5 21h-5.5a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v6" {...strokeProps} />
      <path d="M16 3v4" {...strokeProps} />
      <path d="M8 3v4" {...strokeProps} />
      <path d="M4 11h16" {...strokeProps} />
      <path d="M15 19l2 2l4 -4" {...strokeProps} />
    </>,
  ),
  /* tabler: clock-hour-4 */
  attendance: icon(
    <>
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" {...strokeProps} />
      <path d="M12 12l3 2" {...strokeProps} />
      <path d="M12 7v5" {...strokeProps} />
    </>,
  ),
  /* tabler: checklist */
  worklog: icon(
    <>
      <path d="M9.615 20h-2.615a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8" {...strokeProps} />
      <path d="M14 19l2 2l4 -4" {...strokeProps} />
      <path d="M9 8h4" {...strokeProps} />
      <path d="M9 12h2" {...strokeProps} />
    </>,
  ),
  /* tabler: cash-banknote */
  payroll: icon(
    <>
      <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" {...strokeProps} />
      <path d="M3 6m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" {...strokeProps} />
      <path d="M18 12l.01 0" {...strokeProps} />
      <path d="M6 12l.01 0" {...strokeProps} />
    </>,
  ),
  /* tabler: headset */
  serviceDesk: icon(
    <>
      <path d="M4 14v-3a8 8 0 1 1 16 0v3" {...strokeProps} />
      <path d="M18 19c0 1.657 -2.686 3 -6 3" {...strokeProps} />
      <path d="M4 14a2 2 0 0 1 2 -2h1a2 2 0 0 1 2 2v3a2 2 0 0 1 -2 2h-1a2 2 0 0 1 -2 -2v-3" {...strokeProps} />
      <path d="M15 14a2 2 0 0 1 2 -2h1a2 2 0 0 1 2 2v3a2 2 0 0 1 -2 2h-1a2 2 0 0 1 -2 -2v-3" {...strokeProps} />
    </>,
  ),
  /* tabler: mail */
  correspondence: icon(
    <>
      <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10" {...strokeProps} />
      <path d="M3 7l9 6l9 -6" {...strokeProps} />
    </>,
  ),
  /* tabler: building-community */
  meetings: icon(
    <>
      <path d="M8 9l5 5v7h-5v-4m0 4h-5v-7l5 -5m1 1v-6a1 1 0 0 1 1 -1h10a1 1 0 0 1 1 1v17h-8" {...strokeProps} />
      <path d="M13 7l0 .01" {...strokeProps} />
      <path d="M17 7l0 .01" {...strokeProps} />
      <path d="M17 11l0 .01" {...strokeProps} />
      <path d="M17 15l0 .01" {...strokeProps} />
    </>,
  ),
  /* tabler: shopping-cart */
  procureToPay: icon(
    <>
      <path d="M4 19a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" {...strokeProps} />
      <path d="M15 19a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" {...strokeProps} />
      <path d="M17 17h-11v-14h-2" {...strokeProps} />
      <path d="M6 5l14 1l-1 7h-13" {...strokeProps} />
    </>,
  ),
  /* tabler: user-plus */
  lifecycle: icon(
    <>
      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" {...strokeProps} />
      <path d="M16 19h6" {...strokeProps} />
      <path d="M19 16v6" {...strokeProps} />
      <path d="M6 21v-2a4 4 0 0 1 4 -4h4" {...strokeProps} />
    </>,
  ),
  /* tabler: search */
  search: icon(
    <>
      <path d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" {...strokeProps} />
      <path d="M21 21l-6 -6" {...strokeProps} />
    </>,
  ),
  /* tabler: bell */
  bell: icon(
    <>
      <path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" {...strokeProps} />
      <path d="M9 17v1a3 3 0 0 0 6 0v-1" {...strokeProps} />
    </>,
  ),
  /* tabler: calendar-event — تقویم سازمانی
     ⚠️ عمداً با آیکون «مرخصی» (calendar-check) فرق دارد: هر دو تقویم‌اند
     ولی یکی درخواست است و دیگری تعریفِ روزهای کاری. آیکون یکسان یعنی
     کاربر در سایدبار دوتایشان را یکی ببیند. */
  calendar: icon(
    <>
      <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z" {...strokeProps} />
      <path d="M16 3v4" {...strokeProps} />
      <path d="M8 3v4" {...strokeProps} />
      <path d="M4 11h16" {...strokeProps} />
      <path d="M8 15h2v2h-2z" {...strokeProps} />
    </>,
  ),
  /* tabler: bell-cog — تنظیم اعلان‌ها
     ⚠️ عمداً با `bell` فرق دارد: آن یکی مرکزِ اعلان در نوار بالاست
     («خبرهای من») و این تنظیمِ سازمانی («چه کسی چه خبری بگیرد»).
     آیکون یکسان یعنی کاربر این دو را یکی ببیند. */
  notificationPolicy: icon(
    <>
      <path d="M15 17h-11a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6a2 2 0 1 1 4 0a7 7 0 0 1 4 6" {...strokeProps} />
      <path d="M9 17v1a3 3 0 0 0 3.5 2.95" {...strokeProps} />
      <path d="M19.001 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" {...strokeProps} />
      <path d="M19.001 15.5v1.5" {...strokeProps} />
      <path d="M19.001 21v1.5" {...strokeProps} />
      <path d="M22.032 17.25l-1.299 .75" {...strokeProps} />
      <path d="M17.27 20l-1.3 .75" {...strokeProps} />
      <path d="M15.97 17.25l1.3 .75" {...strokeProps} />
      <path d="M20.733 20l1.3 .75" {...strokeProps} />
    </>,
  ),
} as const

/* ------------------------------------------------------------------
   صفحه موقت ماژول‌های هنوز مهاجرت‌نکرده
   ------------------------------------------------------------------ */

const PeopleModule = lazy(() => import('@/modules/people'))
const AccessModule = lazy(() => import('@/modules/access'))
const CalendarModule = lazy(() => import('@/modules/calendar'))
const AttendanceModule = lazy(() => import('@/modules/attendance'))
const LeaveModule = lazy(() => import('@/modules/leave'))
const PayrollModule = lazy(() => import('@/modules/payroll'))
/* ------------------------------------------------------------------
   ✅ هر ده ماژول مهاجرت کرده‌اند — پایان فاز ۷.

   `PendingModulePage` تا امروز جای‌نگهدارِ ماژول‌های نساخته بود. حالا
   هیچ ماژولی به آن اشاره نمی‌کند و `enabled: false` در این فایل وجود
   ندارد.

   ⚠️ خودِ فایل صفحه عمداً حذف نشده: اگر ماژول تازه‌ای اضافه شود،
   دوباره لازم می‌شود و بازنوشتنش کارِ بی‌دلیل است.
   ------------------------------------------------------------------ */
const WorklogModule = lazy(() => import('@/modules/worklog'))
const ServiceDeskModule = lazy(() => import('@/modules/service-desk'))
const MeetingsModule = lazy(() => import('@/modules/meetings'))
const CorrespondenceModule = lazy(() => import('@/modules/correspondence'))
const ProcurementModule = lazy(() => import('@/modules/procurement'))
const LifecycleModule = lazy(() => import('@/modules/lifecycle'))

/* ------------------------------------------------------------------
   ثبت ۹ ماژول کسب‌وکار
   ترتیب و فاز از MIGRATION_BACKLOG.md گرفته شده است.
   ------------------------------------------------------------------ */

export const MODULES: ModuleDefinition[] = [
  {
    key: 'worklog',
    title: 'گزارش کار',
    path: 'worklog',
    icon: ICONS.worklog,
    /* ⚠️ رندرهای دارای متنِ پخته‌شده اینجا استفاده نمی‌شوند: در ۵۶px
       نوشته‌شان ناخوانا می‌شود و کنار برچسب کاشی هم تکراری است. */
    icon3d: 'page-worklog-reports',
    owner: 'مدیران واحدها',
    tagline: 'ثبت تایم‌شیت روزانه',
    sourceProject: 'hrms-dailyrports',
    phase: 4,
    enabled: true,
    component: WorklogModule,
    order: 10,
    children: [
      { title: 'داشبورد من', path: '', icon3d: 'page-worklog-my' },
      { title: 'گزارش امروز', path: 'today', permission: 'worklog.daily_report.create', icon3d: 'page-worklog-daily' },
      { title: 'گزارش‌ها', path: 'reports', icon3d: 'page-worklog-reports' },
      { title: 'نمای تیم', path: 'team', anyOf: ['worklog.daily_report.view_team', 'worklog.daily_report.view_unit', 'worklog.daily_report.view_all'], icon3d: 'page-worklog-team' },
      { title: 'گزارش هفتگی', path: 'weekly', permission: 'worklog.weekly_report.create', icon3d: 'page-worklog-weekly' },
      { title: 'گزارش‌های هفتگی', path: 'weekly/list', anyOf: ['worklog.weekly_report.create', 'worklog.weekly_report.approve', 'worklog.weekly_report.return'], icon3d: 'page-worklog-weekly' },
      { title: 'اجازهٔ گزارش هفتگی', path: 'weekly/access', permission: 'worklog.weekly_report.extend_deadline' },
      /* بخش‌های بدون منو — فقط برای نگاشت آیکون.
         `hidden` نگهشان می‌دارد از فهرست سایدبار بیرون. */
      { title: 'جزئیات گزارش', path: 'daily', icon3d: 'page-worklog-detail', hidden: true },
    ],
  },
  {
    key: 'people',
    title: 'پرونده پرسنلی',
    path: 'people',
    icon: ICONS.people,
    icon3d: 'people',
    owner: 'منابع انسانی',
    tagline: 'اطلاعات هویتی و قرارداد',
    sourceProject: 'hrms-parvande-personelli',
    phase: 5,
    enabled: true,
    component: PeopleModule,
    order: 20,
    children: [
      { title: 'کارکنان', path: 'employees', icon3d: 'page-employee-profile' },
      /* ⚠️ بلندتر از `employees` است پس در تطبیق زودتر بررسی می‌شود و
         فرم ثبت، آیکون خودش را می‌گیرد نه آیکون فهرست. */
      { title: 'ثبت کارمند جدید', path: 'employees/new', icon3d: 'page-employee-new', hidden: true },
      /* 🔴 ۱۴۰۵/۰۸/۱۱ صاحبِ پروژه: «چارتِ سازمانی از پروندهٔ پرسنلی دربیاد و بیاد زیرِ گفتگو و بالای تنظیمِ اعلان» —
         پیوندش در سطحِ اولِ سایدبار است (Sidebar.tsx)؛ مسیر همان ‎/app/people/org-chart‎ می‌ماند و این ردیف
         فقط برای آیکون و عنوانِ صفحه. */
      { title: 'چارت سازمانی', path: 'org-chart', permission: 'people.org_chart.view', icon3d: 'page-org-chart', hidden: true },
      { title: 'داشبورد منابع انسانی', path: 'dashboard', permission: 'people.dashboard.view', icon3d: 'page-people-dashboard' },
    ],
  },
  {
    key: 'leave',
    title: 'مرخصی',
    path: 'leave',
    icon: ICONS.leave,
    icon3d: 'leave',
    owner: 'منابع انسانی',
    tagline: 'درخواست و سوابق',
    sourceProject: 'hr-system',
    phase: 5,
    enabled: true,
    component: LeaveModule,
    order: 30,
    children: [
      { title: 'مرخصی من', path: '', icon3d: 'leave' },
      { title: 'ثبت درخواست', path: 'new', permission: 'leave.request.create', icon3d: 'page-leave-new' },
      { title: 'کارتابل تأیید', path: 'approvals', permission: 'leave.request.approve', icon3d: 'page-leave-approvals' },
      /* ⚠️ بدون شرط مجوز — جانشین‌بودن نقش سازمانی نیست. */
      { title: 'کارتابل جانشینی', path: 'substitute', icon3d: 'page-leave-substitute' },
      { title: 'تقویم تیم', path: 'calendar', icon3d: 'page-leave-calendar' },
      /* 🔴 خروجی اکسل مرخصی همهٔ کارکنان — درخواست واحد مالی. */
      { title: 'گزارش مرخصی', path: 'reports', permission: 'leave.report.export' },
      { title: 'جزئیات درخواست', path: 'requests', icon3d: 'page-leave-detail', hidden: true },

      /*
       | مأموریت — تصمیم M-07: «درخواست مأموریت در بخش مرخصی خودش را
       | نشان می‌دهد، اما گزارش آن در لیست تردد روزانه می‌افتد.»
       |
       | ⚠️ برای همین اینجاست و نه در «حضور و غیاب» — همان‌جا هم صریحاً
       | توضیح داده شده که چرا در فهرستِ آن ماژول نیست.
       */
      { title: 'مأموریت‌ها', path: 'missions' },
      { title: 'ثبت مأموریت', path: 'missions/new', permission: 'leave.request.create', hidden: true },
    ],
  },
  /*
   | حقوق و دستمزد — فعلاً فقط «درخواست مساعده» (فرم کاغذی FIF-13).
   |
   | 🔴 از ۱۴۰۵/۰۶/۱۸ (‎2026-09-09) برای نقش‌های کسب‌وکار باز شد و
   | `superAdminOnly` برداشته شد — تصمیم کارفرما. پیش از این مثل آن پنج
   | ماژول دیگر فقط «مدیر سیستم» می‌دیدش.
   |
   | ⚠️ برداشتنِ این پرچم به‌تنهایی کافی نیست: ماتریسِ نقشِ
   | `PayrollPermissions::roleMatrix()` هم باید مجوزها را به نقش‌ها
   | بدهد و Seeder اجرا شود، وگرنه منو باز می‌شود و صفحه ۴۰۳ می‌دهد.
   |
   | ⚠️ «کارتابل» با `anyOf` اعلام شده و نه یک مجوز: تأییدکننده و
   | صندوق‌دار دو نقشِ متفاوت‌اند و هر دو به همان صفحه می‌روند. با یک
   | مجوزِ تنها، صندوق‌دار منویی نمی‌دید که تنها کارش آنجاست.
   |
   | 🔴 «مساعده من» و «جزئیات» عمداً مجوزی اعلام نمی‌کنند، پس در
   | `moduleDeclaredPermissions` نمی‌آیند. یعنی دسترسیِ ماژول از روی سه
   | کلیدِ دیگر سنجیده می‌شود — و هر کارمندی `payroll.advance.create`
   | را دارد.
   */
  {
    key: 'payroll',
    title: 'حقوق و دستمزد',
    path: 'payroll',
    icon: ICONS.payroll,
    icon3d: 'page-payroll',
    owner: 'مالی',
    tagline: 'مساعده و پرداخت‌ها',
    sourceProject: '—',
    phase: 5,
    enabled: true,
    component: PayrollModule,
    order: 35,
    children: [
      { title: 'مساعده من', path: '' },
      { title: 'ثبت درخواست', path: 'new', permission: 'payroll.advance.create' },
      {
        title: 'کارتابل مساعده',
        path: 'inbox',
        anyOf: ['payroll.advance.approve', 'payroll.advance.deliver'],
      },
      { title: 'گزارش مساعده', path: 'report', permission: 'payroll.report.view' },
      /* 🔴 حقوق هر نفر — درخواست واحد مالی. */
      { title: 'حقوق من', path: 'salary', permission: 'payroll.salary.view_self' },
      {
        title: 'حقوق کارکنان',
        path: 'salaries',
        anyOf: ['payroll.salary.view_team', 'payroll.salary.view_all', 'payroll.salary.manage'],
      },
      { title: 'جزئیات درخواست', path: 'advances', hidden: true },
    ],
  },
  {
    key: 'attendance',
    title: 'حضور و غیاب',
    path: 'attendance',
    icon: ICONS.attendance,
    icon3d: 'page-attendance-console',
    owner: 'منابع انسانی',
    tagline: 'ترددها و شیفت‌ها',
    sourceProject: 'hr-dailycheck',
    phase: 5,
    enabled: true,
    component: AttendanceModule,
    order: 40,
    /*
     | ⚠️ «مأموریت» عمداً در این فهرست نیست.
     |
     | تصمیم کارفرما: درخواست مأموریت در ماژول مرخصی است و فقط
     | *نتیجه‌اش* در «تردد من» به‌عنوان پوشش روز دیده می‌شود. گذاشتن
     | مسیرش اینجا یعنی کاربر دو جای متفاوت برای یک کار پیدا کند.
     |
     | ولی «دورکاری» اینجاست — کاملاً، هم درخواست هم نمایش.
     */
    children: [
      { title: 'تردد من', path: '', icon3d: 'page-attendance-my' },
      { title: 'دورکاری', path: 'remote-work', permission: 'attendance.remote.request', icon3d: 'page-remote-work' },
      /*
       | 🔴 کنسول کنترل تردد — زیرمنوی همین ماژول، طبق تصمیم کارفرما.
       |
       | مدیرعامل، مدیر مالی و کارشناس مالی اینجا «مثل ادمین» عمل
       | می‌کنند: تنظیمات، رسیدگی به درخواست اصلاح، و ورود فایل اکسل.
       */
      /*
       | ⚠️ `anyOf` و نه فقط `adjustment.approve`: کارشناسی که فقط فایل
       | دستگاه را وارد می‌کند (یا فقط ویرایش تردد دارد) هم باید برسد؛
       | زبانه‌هایی که مجوزش را ندارد خودشان پنهان می‌شوند.
       */
      {
        title: 'کنسول کنترل تردد',
        path: 'console',
        anyOf: ['attendance.adjustment.approve', 'attendance.punch.import', 'attendance.punch.edit'],
        icon3d: 'page-attendance-console',
      },
    ],
  },
  /*
   | ==================================================================
   | 🔴 پنج ماژول «خدمات و پشتیبانی»، «جلسات»، «دبیرخانه»، «خرید و
   | پرداخت» و «جذب تا خروج» فقط در اختیار «مدیر سیستم»‌اند.
   |
   | تا دیروز کلاً بسته بودند (`enabled => false` در سمت سرور و حذف از
   | همین فهرست). حالا کارفرما خواست برای مدیر سیستم باز باشند، پس:
   |
   |   ۱. در apps/api/config/modules.php دوباره `enabled => true` شدند —
   |      وگرنه routeهایشان اصلاً ثبت نمی‌شود و صفحه‌ها ۴۰۴ می‌گیرند.
   |   ۲. اینجا با `superAdminOnly` برگشتند، پس برای بقیهٔ کاربران در
   |      منو، کاشیِ داشبورد، جست‌وجوی فرمان و حتی مسیر ساخته نمی‌شوند.
   |   ۳. مجوزهایشان در `PermissionCatalog` به نقش system_admin داده شد،
   |      وگرنه منو باز می‌شد ولی هر صفحه ۴۰۳ می‌داد.
   |
   | ⚠️ بندِ ۷.۲ / SG-15 هنوز برقرار است: کلیدهای
   | `confidentialContent()` — نامهٔ محرمانه، کد ملی، اجرای پرداخت —
   | عمداً داده *نشده‌اند*. `RoleSeeder` هم اگر داده شوند خطا می‌دهد.
   |
   | 🔴 و مثل همیشه، این فیلتر فقط نمایش است. کنترل واقعی در Policyهای
   | Backend است (بند ۱۱.۱).
   | ==================================================================
   */
  {
    key: 'service_desk',
    title: 'خدمات و پشتیبانی',
    path: 'service-desk',
    icon: ICONS.serviceDesk,
    icon3d: 'page-ticket-list',
    owner: 'IT / خدمات سازمانی',
    tagline: 'تیکتینگ داخلی',
    sourceProject: 'hrms-ITHELPDESK',
    phase: 6,
    enabled: true,
    component: ServiceDeskModule,
    order: 50,
    /*
     | ⚠️ «تیکت‌های من» و «صف کاری» عمداً یک مسیرند.
     |
     | تفکیکشان را سرور از روی دامنه دید انجام می‌دهد. اگر دو آیتم منو
     | می‌گذاشتیم، کاربری که هر دو نقش را دارد باید بین دو صفحه
     | جابه‌جا می‌شد تا یک فهرست را ببیند — و هر فیلتری را دو بار
     | می‌زد.
     */
    /*
     | ⚠️ `superAdminOnly` برداشته شد.
     |
     | 🔴 ثبت تیکت کارِ روزمرهٔ هر کارمند است، نه ابزار اداری. تا وقتی
     | ماژول از منو پنهان بود، `ticket.create` و `ticket.view_self` که
     | نقشِ `employee` از قبل داشت فقط روی کاغذ بودند — مسیر بسته
     | می‌شد و هیچ صفحه‌ای به آن‌ها نمی‌رسید.
     |
     | ⚠️ `alsoVisibleToRoles: ['helpdesk']` هم با آن حذف شد: استثنا
     | فقط تا وقتی معنا داشت که قاعده‌ای برای استثنا کردن بود.
     |
     | ⚠️ دسترسی همچنان کلید‌به‌کلید سنجیده می‌شود و سرور دامنهٔ دید را
     | مستقل محدود می‌کند (بند ۷.۲).
     */
    children: [
      { title: 'درخواست‌ها', path: '', icon3d: 'page-ticket-list' },
      { title: 'ثبت درخواست', path: 'new', permission: 'service_desk.ticket.create', icon3d: 'page-ticket-new' },
      /*
       | ⚠️ کلیدِ منو `catalog.manage` است و نه `catalog.view`.
       |
       | 🔴 `catalog.view` معیارِ درستی برای این منو نیست: تقریباً همهٔ
       | نقش‌ها دارندش، چون همان مجوز انتخابگرِ خدمت را در «ثبت
       | درخواست» باز می‌کند. اگر ملاک می‌شد، پیکربندیِ کاتالوگ در
       | منوی هر کارمندی ظاهر می‌شد.
       |
       | پس این صفحه فقط برای هلپ‌دسک و مدیر سیستم است — همان دو نقشی
       | که `manage` دارند.
       */
      { title: 'کاتالوگ خدمات', path: 'catalog', permission: 'service_desk.catalog.manage', icon3d: 'page-ticket-list' },
      { title: 'جزئیات درخواست', path: 'tickets', icon3d: 'page-ticket-detail', hidden: true },
    ],
  },
  {
    key: 'meetings',
    title: 'جلسات',
    path: 'meetings',
    icon: ICONS.meetings,
    icon3d: 'meetings',
    owner: 'دفتر مدیریت',
    tagline: 'مدیریت اتاق جلسات',
    sourceProject: 'meeting',
    phase: 6,
    enabled: true,
    component: MeetingsModule,
    order: 60,
    /*
     | ⚠️ `superAdminOnly` برداشته شد.
     |
     | 🔴 کارمند حالا می‌تواند جلسهٔ خودش را کامل بگرداند، و اگر ماژول
     | از منو پنهان می‌ماند، آن اختیار فقط روی کاغذ بود: مسیر بسته
     | می‌شد و هیچ صفحه‌ای به این قابلیت‌ها نمی‌رسید.
     |
     | ⚠️ دسترسی همچنان کلید‌به‌کلید سنجیده می‌شود — هر آیتمِ منو
     | `permission` یا `anyOf` خودش را دارد و سرور هم دامنه را به
     | جلسهٔ خودِ کاربر محدود می‌کند (`authorizeMeeting`).
     */
    children: [
      { title: 'جلسات', path: '', icon3d: 'page-meetings-list' },
      { title: 'رزرو اتاق جلسه', path: 'rooms', permission: 'meetings.meeting.create' },
      { title: 'جلسه جدید', path: 'new', permission: 'meetings.meeting.create', icon3d: 'page-meetings-new' },
      /*
        🔴 «اقدام‌ها» و نه «مصوبات».

        ⚠️ این صفحه از روز اول فهرستِ `action_items` بود ولی «مصوبات»
        نام داشت — و حالا که فهرستِ واقعیِ مصوبه‌ها هم اضافه شده، دو
        آیتمِ منو با نامِ تقریباً یکسان کنار هم می‌نشستند و کاربر
        نمی‌فهمید کدام کدام است.
      */
      { title: 'اقدام‌ها', path: 'action-items', icon3d: 'page-meetings-actions' },
      /*
        🔴 درخواست جلسه از روز اول در Backend کامل بود — ده وضعیت،
        هفت تصمیم، و ۱۱ تست — ولی هیچ آیتمی در منو به آن نمی‌رسید.

        ⚠️ `anyOf` و نه `permission`: کارمند با `view_self` فقط
        درخواست‌های خودش را می‌بیند و بررسی‌کننده با `view_all` همه را.
        اگر فقط یکی را شرط می‌کردیم، یکی از این دو گروه منو را
        نمی‌دید.
      */
      {
        title: 'درخواست‌های جلسه',
        path: 'requests',
        anyOf: ['meetings.request.view_self', 'meetings.request.view_all'],
      },
      { title: 'درخواست جدید', path: 'requests/new', permission: 'meetings.request.create' },
      /*
        ⚠️ «مصوبه‌ها» با «مصوبات» بالا یکی نیست: آن فهرستِ **اقدام‌ها**
        است و این فهرستِ **تصمیم‌ها**. تصمیم چیزی است که تصویب شد؛
        اقدام کارِ اجراییِ ذیلِ آن.
      */
      { title: 'مصوبه‌ها', path: 'decisions', permission: 'meetings.decision.view' },
    ],
  },
  {
    key: 'correspondence',
    title: 'دبیرخانه',
    path: 'correspondence',
    icon: ICONS.correspondence,
    icon3d: 'page-correspondence',
    owner: 'دبیرخانه',
    tagline: 'نامه‌های وارده و صادره',
    sourceProject: 'hrms-dabirkhane',
    phase: 6,
    enabled: true,
    component: CorrespondenceModule,
    order: 70,
    /*
     | ⚠️ `superAdminOnly` برداشته شد.
     |
     | 🔴 کارمند نامهٔ خودش را ثبت می‌کند و روی نامه‌ای که به او ارجاع
     | شده اقدام می‌نویسد — کلیدهای `letter.create`، `letter.view_self`
     | و `action.record` از قبل در نقشِ `employee` بودند. پنهان‌ماندنِ
     | ماژول یعنی «این نامه چه شد؟» فقط از دبیرخانه پاسخ می‌گرفت.
     |
     | ⚠️ نامه‌های محرمانه از این راه باز نمی‌شوند: کلیدهای
     | `confidentialContent()` جدا می‌مانند (بند ۷.۲ / SG-15).
     */
    children: [
      /* 🔴 ۱۴۰۵/۰۸/۱۱ صاحبِ پروژه: «در سایدبار کلمهٔ نامه‌ها بشه بایگانی نامه‌ها» */
      { title: 'بایگانی نامه‌ها', path: '', icon3d: 'page-letter-list' },
      { title: 'ثبت نامه', path: 'new', permission: 'correspondence.letter.create', icon3d: 'page-letter-new' },
      { title: 'کارتابل من', path: 'inbox', icon3d: 'page-correspondence-inbox' },
      /*
       | 🔴 شمارهٔ دفتر اداری برای قراردادی که نامه نیست.
       | ⚠️ زیر `letter.create` — هر کس به دبیرخانه دسترسی دارد (تصمیم کارفرما).
       */
      { title: 'شماره قرارداد', path: 'contracts', permission: 'correspondence.letter.create' },
      /*
       | 🔴 دفترچهٔ طرف‌های بیرونی — تا امروز هیچ صفحه‌ای نداشت.
       |
       | ⚠️ فرمِ نامه کد سازمان را متنِ آزاد می‌گرفت و کاربر باید از
       | حفظ می‌نوشتش. یعنی ثبت نامه برای سازمانِ تازه ناممکن بود.
       */
      { title: 'طرف‌های بیرونی', path: 'directory', permission: 'correspondence.contact.view' },
    ],
  },
  {
    key: 'procure_to_pay',
    title: 'خرید و پرداخت',
    path: 'procurement',
    icon: ICONS.procureToPay,
    icon3d: 'payroll',
    owner: 'تدارکات و مالی',
    tagline: 'درخواست خرید و پرداخت',
    sourceProject: 'hrms',
    phase: 7,
    enabled: true,
    component: ProcurementModule,
    order: 80,
    /*
     | ⚠️ `superAdminOnly` برداشته شد.
     |
     | 🔴 درخواست خرید از پایین شروع می‌شود: کارمند می‌خواهد، مدیر
     | تأیید می‌کند. نقشِ `employee` از قبل `request.create` و
     | `request.view_self` را داشت و بدونِ منو هیچ‌کدام در دسترس نبود.
     |
     | ⚠️ آیتم‌های مالی — سفارش، فاکتور، پرداخت و تأمین‌کننده — هرکدام
     | `permission` خودشان را دارند و برای کارمند عادی پنهان می‌مانند.
     | `payment.execute` هم محرمانه است و اصلاً اعطا نمی‌شود.
     */
    children: [
      { title: 'درخواست‌های خرید', path: '' },
      { title: 'درخواست جدید', path: 'new', permission: 'procure_to_pay.request.create' },
      { title: 'سفارش‌های خرید', path: 'orders', permission: 'procure_to_pay.purchase_order.view' },
      /*
       | ⚠️ ورودیِ «فاکتورها» حذف شد — فاکتور در درخواست پرداخت ادغام
       | شد و دو ورودیِ منو برای یک کار، همان چیزی بود که کاربر را
       | سردرگم می‌کرد.
       |
       | ⚠️ زیر `payment.view_self` است و نه `payment.view`.
       |
       | 🔴 `payment.view` را هر کارمندی دارد — شمارهٔ حساب تأمین‌کننده و
       | استعلامِ حواله رویش سوارند. ولی فهرستِ درخواست‌های پرداخت
       | دامنه دارد، و کمینهٔ آن `view_self` است: همان کلیدی که سرور
       | برای این فهرست می‌سنجد.
       |
       | ⚠️ منو برای همه باز می‌ماند (هر کارمندی `view_self` دارد) ولی
       | آنچه می‌بیند فقط درخواست‌های خودش است.
       */
      {
        title: 'درخواست پرداخت',
        path: 'payment-requests',
        permission: 'procure_to_pay.payment.view_self',
      },
      { title: 'تأمین‌کنندگان', path: 'vendors', permission: 'procure_to_pay.vendor.view' },
      /*
       | ⚠️ «اطلاعات پایه» مجوز جداگانه اعلام نمی‌کند و همان دسترسیِ
       | ماژول کافی است.
       |
       | 🔴 خواندنِ سرفصل و پروژه با همان مجوزِ دیدنِ درخواست است —
       | سرور هم همین‌طور می‌سنجد. اعلامِ کلیدِ مدیریت اینجا یعنی
       | کاربری که فقط می‌بیند، منو را هم نبیند.
       */
      { title: 'اطلاعات پایه', path: 'basics' },
      { title: 'گزارش‌ها', path: 'reports', permission: 'procure_to_pay.report.view' },
    ],
  },
  {
    key: 'employee_lifecycle',
    title: 'جذب تا خروج',
    path: 'lifecycle',
    icon: ICONS.lifecycle,
    icon3d: 'recruitment',
    owner: 'منابع انسانی',
    tagline: 'جذب، استخدام و خروج',
    sourceProject: 'hrms-onbording',
    phase: 7.5,
    enabled: true,
    component: LifecycleModule,
    order: 90,
    /* فقط مدیر سیستم — ماژول برای بقیه اصلاً ساخته نمی‌شود.
       توضیح کامل روی خودِ فیلد در `ModuleDefinition` است. */
    superAdminOnly: true,
    /*
     | ترتیب فهرست، ترتیب خودِ چرخه است: از درخواست نیرو تا خروج.
     |
     | ⚠️ «پرونده‌ها» با «فرآیند ورود و خروج» یکی نیست و هر دو می‌مانند:
     | فرآیند چک‌لیست عمومی است، پرونده همان چیزی است که دارایی،
     | دسترسی، تسویه و مصاحبهٔ خروج به آن بسته می‌شوند.
     */
    children: [
      { title: 'متقاضیان', path: '', permission: 'employee_lifecycle.candidate.view' },
      {
        title: 'درخواست نیرو',
        path: 'requisitions',
        permission: 'employee_lifecycle.requisition.view',
      },
      { title: 'آگهی‌های شغلی', path: 'openings', permission: 'employee_lifecycle.opening.view' },
      {
        title: 'جذب و مصاحبه',
        path: 'applications',
        permission: 'employee_lifecycle.application.view',
      },
      {
        title: 'بانک استعداد',
        path: 'talent-pool',
        permission: 'employee_lifecycle.candidate.view',
      },
      { title: 'فرآیند ورود و خروج', path: 'processes' },
      {
        title: 'پرونده‌های ورود و خروج',
        path: 'cases',
        permission: 'employee_lifecycle.onboarding.view_all',
      },
      {
        title: 'دوره‌های آزمایشی',
        path: 'probations',
        permission: 'employee_lifecycle.probation.view',
      },
      {
        title: 'قالب‌های چک‌لیست',
        path: 'checklists',
        permission: 'employee_lifecycle.checklist.view',
      },
      { title: 'وظایف من', path: 'tasks' },
    ],
  },
  /*
   | ==================================================================
   | 🔴 سطح دسترسی — ماژول هسته، نه کسب‌وکار.
   |
   | مجوزهایش با پیشوند `platform.` است و روی همه ماژول‌ها اثر دارد.
   | عمداً آخرین مورد فهرست است (order 100): کاری است که کم پیش می‌آید
   | ولی وقتی لازم شود باید فوراً پیدا شود.
   |
   | ⚠️ `phase: 1` چون بخشی از هسته است و از فاز اول وجود داشته —
   | فقط رابط کاربری‌اش تا امروز ساخته نشده بود.
   | ==================================================================
   */
  {
    key: 'access',
    title: 'سطح دسترسی',
    path: 'access',
    icon: ICONS.people,
    icon3d: 'page-access',
    owner: 'معماری پلتفرم',
    tagline: 'نقش‌ها و مجوزها',
    sourceProject: 'platform-core',
    phase: 1,
    enabled: true,
    component: AccessModule,
    order: 100,
    /* فقط مدیر سیستم — به درخواست کارفرما. توضیح کامل روی خودِ فیلد
       در `ModuleDefinition` است. */
    superAdminOnly: true,
    children: [
      { title: 'نقش‌ها', path: '', permission: 'platform.role.view' },
    ],
  },

  /*
   | ==================================================================
   | 🔴 تقویم سازمانی — ماژول هسته، نه کسب‌وکار.
   |
   | پایهٔ محاسبهٔ مهلت و مرخصی در همهٔ ماژول‌هاست: `WorkCalendar` از
   | همین جدول‌ها می‌خواند.
   |
   | ⚠️ `order: 99` — درست پیش از «سطح دسترسی». هر دو کارِ پیکربندیِ
   | سازمان‌اند و باید کنار هم و ته فهرست باشند، نه لابه‌لای کارِ روزمره.
   |
   | ⚠️ `phase: 1` چون جدول‌ها و مجوزهایش از فاز اول وجود داشتند —
   | فقط Endpoint و صفحه‌اش تا امروز ساخته نشده بود، و هر دو مجوزش در
   | فهرست «کلید مردهٔ اعلام‌شده» نشسته بود.
   | ==================================================================
   */
  {
    key: 'calendar',
    title: 'تقویم سازمانی',
    path: 'calendar',
    icon: ICONS.calendar,
    icon3d: 'calendar',
    owner: 'معماری پلتفرم',
    tagline: 'روزهای کاری و تعطیلات رسمی',
    sourceProject: 'platform-core',
    phase: 1,
    enabled: true,
    component: CalendarModule,
    order: 99,
    children: [
      { title: 'تقویم سال', path: '', permission: 'platform.calendar.view' },
    ],
  },
]

export function findModuleByPath(path: string): ModuleDefinition | undefined {
  return MODULES.find((module) => module.path === path)
}

export function findModuleByKey(key: string): ModuleDefinition | undefined {
  return MODULES.find((module) => module.key === key)
}

/**
 * همهٔ کلیدهای مجوزی که یک ماژول برای بخش‌هایش اعلام کرده.
 *
 * ==================================================================
 * 🔴 چرا این تابع وجود دارد — و چرا جای دیگری نباید تکرار شود:
 *
 * دسترسی به ماژول پیش‌تر از روی **پیشوندِ نام ماژول** حدس زده می‌شد
 * (`access.` برای ماژول `access`). ولی «سطح دسترسی» کلیدهایش
 * `platform.role.view` است و «تقویم سازمانی» کلیدش
 * `platform.calendar.view` — هیچ‌کدام با پیشوندِ نام ماژول شروع
 * نمی‌شوند.
 *
 * ⚠️ اصلاحِ اولِ این نقص فقط به `RequireModule` رسید. سایدبار و کاشیِ
 * داشبورد همچنان بدون کلید صدا می‌زدند، پس هر دو ماژول **خاکستری و
 * غیرقابل‌کلیک** می‌ماندند — کاربر نشانگرِ «ممنوع» می‌دید و هیچ توضیحی
 * نبود.
 *
 * 🔴 نقصِ اصلی این بود که یک محاسبه در دو جا نوشته شده بود و فقط یکی
 * اصلاح شد. حالا یک تعریف است و `AuthProvider` وقتی فراخوان چیزی
 * ندهد، خودش از همین‌جا می‌خواندش — پس دیگر «یادش رفت» ممکن نیست.
 * ==================================================================
 */
export function moduleDeclaredPermissions(moduleKey: string): string[] {
  const module = findModuleByKey(moduleKey)

  if (!module) return []

  return module.children.flatMap((child) => [
    ...(child.permission ? [child.permission] : []),
    ...(child.anyOf ?? []),
  ])
}

/* ------------------------------------------------------------------
   آیکون سرصفحهٔ مسیر جاری
   ------------------------------------------------------------------
   مسیر کامل (`/app/leave/approvals`) را می‌گیرد و نشانی آیکون را
   برمی‌گرداند: اول دنبال بخشِ دقیقاً منطبق می‌گردد، بعد به آیکون خود
   ماژول برمی‌گردد.

   🔴 چرا اینجا و نه داخل هر صفحه: نگاشتِ «کدام صفحه کدام آیکون» یک بار
   در همین رجیستری تعریف می‌شود — همان‌جایی که مسیر و عنوان و مجوز هم
   تعریف شده‌اند. اگر هر صفحه خودش نشانی فایل را می‌نوشت، ۳۳ نقطهٔ
   پراکنده داشتیم که با تغییر نام یک فایل بی‌صدا می‌شکستند.

   ⚠️ تطبیق بخش‌ها از بلندترین مسیر شروع می‌شود: مسیر `''` (صفحهٔ
   landing) با هر چیزی جور درمی‌آید، پس اگر اول بررسی شود همیشه برنده
   می‌شود و بخش‌های داخلی هرگز آیکون خودشان را نمی‌گیرند.
   ------------------------------------------------------------------ */
function findPageMatch(
  pathname: string,
): { module: ModuleDefinition; child?: ModuleChild; isLanding: boolean } | undefined {
  const [, , modulePath = '', ...rest] = pathname.split('/')
  const module = findModuleByPath(modulePath)

  if (!module) return undefined

  const childPath = rest.join('/')

  /* صفحهٔ landing خود ماژول — بخشی با مسیر خالی */
  if (childPath === '') {
    return {
      module,
      child: module.children.find((child) => child.path === ''),
      isLanding: true,
    }
  }

  return {
    module,
    isLanding: false,
    child: [...module.children]
      .filter((child) => child.path !== '')
      .sort((a, b) => b.path.length - a.path.length)
      .find((child) => childPath === child.path || childPath.startsWith(`${child.path}/`)),
  }
}

export function findPageIcon(pathname: string): string | undefined {
  const match = findPageMatch(pathname)

  if (!match) return undefined

  return match.child?.icon3d ?? match.module.icon3d
}

/* ------------------------------------------------------------------
   عنوان تب مرورگر برای مسیر جاری
   ------------------------------------------------------------------
   همان مسیریابیِ آیکون، این بار برای برچسب: اول عنوان بخش، بعد
   عنوان خود ماژول. در صفحهٔ landing این دو یکی‌اند، پس تکرار
   نمی‌شود.

   ⚠️ صفحه‌های جزئیات عنوانِ ثابتِ بخش را می‌گیرند نه نام رکورد را؛
   رجیستری ایستا است و دادهٔ رکورد را نمی‌شناسد.

   🔴 صفحهٔ landing فقط نام ماژول را می‌گیرد. عنوانِ بخشِ خالی همیشه
   گونهٔ دیگری از نام ماژول است («مرخصی من» زیر «مرخصی»)، پس کنار هم
   گذاشتنشان «مرخصی من — مرخصی» می‌داد؛ همان کلمه، دو بار، در تبی که
   جا هم ندارد.
   ------------------------------------------------------------------ */
export function findPageTitle(pathname: string): string | undefined {
  const match = findPageMatch(pathname)

  if (!match) return undefined

  const { module, child, isLanding } = match

  if (isLanding || !child || child.title === module.title) return module.title

  return `${child.title} — ${module.title}`
}
